import { SortDirection } from "@enums/SortDirection";
import { SpecialUserPermissions } from "@enums/SpecialUserPermissions";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser } from "@interfaces/IUser";
import { EnumService } from "@services/enum.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import * as angular from "angular";
import "angular-material";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { AlertCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertRuleRecord } from "../../_models/alerts.interfaces";
import { AlertsService } from "../../_services/alerts.service";
import "./recentAlerts.component.scss";
import { DateService } from "@app/services/date.service";

interface IComponentBindings {
    maxListLength: number;
}

class RecentAlertsComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    maxListLength: number;

    // loaders
    initialLoadComplete: boolean = false;
    loadingRecentAlerts: boolean = false;
    loadingAlertRule: boolean = false;

    // raw data
    alertRuleRecords: IAlertRuleRecord[] = [];
    alertCategories: any[] = [];

    // access
    currentUser: IUser;
    isSystemUser = false;
    canManageAlerts: boolean = false;

    static $inject = ["$q", "$rootScope", "alertsService", "accountFeatureService", "dateService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly alertsService: AlertsService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly dateService: DateService) {

    }

    $onInit() {
        this.maxListLength = this.maxListLength || 5;

        this.initUser();
        this.alertCategories = EnumService.enumToArray("AlertCategory", AlertCategory);
        this.alertCategories.sort((cat1, cat2) => (cat1.value > cat2.value) ? 1 : -1);

        this.refreshData();
    }

    onClickRecentAlertRuleRecord($event: MouseEvent, alertRuleRecord: IAlertRuleRecord) {
        if (!alertRuleRecord || this.loadingAlertRule) {
            return;
        }

        this.loadingAlertRule = true;

        this.alertsService
            .getAlertRule(alertRuleRecord?.RuleId)
            .then(alertRule => {
                if (!alertRule) {
                    return;
                }

                this.alertsService.showAlertEditor(alertRule, $event, {
                    autoLoadedRecordId: alertRuleRecord?.Id,
                    onAlertEditorCancelled: () => { this.onAlertEditorCancelled(); },
                    onAlertEditorDeleted: () => { this.onAlertEditorDeleted(); },
                    onAlertEditorSaved: () => { this.onAlertEditorSaved(); }
                });
            })
            .finally(() => {
                this.loadingAlertRule = false;
            });
    }

    onAlertEditorCancelled(): void {
        this.refreshData();
    }

    onAlertEditorDeleted(): void {
        this.refreshData();
    }

    onAlertEditorSaved(): void {
        this.refreshData();
    }

    private initUser(): void {
        this.currentUser = copy(this.$rootScope.currentUser);
        this.isSystemUser = this.currentUser.CurrentAccountUserMapping.Type === 0;
        this.canManageAlerts = this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ManageAlerts);
    }

    private refreshData(): void {
        if (this.loadingRecentAlerts) {
            return;
        }

        this.loadingRecentAlerts = true;

        this.getData().finally(() => {
            this.initialLoadComplete = true;
            this.loadingRecentAlerts = false;
        });
    }

    private getData(): angular.IPromise<any> {
        const yesterdaysDate: Date = new Date();
        yesterdaysDate.setDate(yesterdaysDate.getDate() - 5);

        const from: Date = new Date(yesterdaysDate);
        const to: Date = new Date;

        const alertRuleRecordsPromise = this.alertsService.getAlertRuleRecordsForAccount(from, to);

        return this.$q.all([alertRuleRecordsPromise]).then(([alertRuleRecords]) => {

            this.alertRuleRecords = this.prepareAlertRuleRecords(alertRuleRecords)
        });
    }

    private prepareAlertRuleRecords(alertRuleRecords: IAlertRuleRecord[] = []): IAlertRuleRecord[] {
        const preparedAlertRuleRecords: IAlertRuleRecord[] = [];

        alertRuleRecords = alertRuleRecords.filter(record => record?.TriggeredAtDataTimestamp && record?.SentCount > 0);
        alertRuleRecords.sort((record1, record2) => new Date(record2.TriggeredAtDataTimestamp).getTime() - new Date(record1.TriggeredAtDataTimestamp).getTime());

        for (var i = 0; i < alertRuleRecords?.length; i++) {
            if (preparedAlertRuleRecords?.length >= this.maxListLength) {
                break;
            }

            const alertRuleRecord: IAlertRuleRecord = alertRuleRecords[i];

            if (alertRuleRecord?.SentCount > 0) {
                preparedAlertRuleRecords.push(alertRuleRecord);
            }
        }

        return preparedAlertRuleRecords;
    }
}

export class RecentAlertsComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            maxListLength: "<",
        };

        this.controller = RecentAlertsComponentController;
        this.template = require("./recentAlerts.component.html");
    }
}
